import Phaser from 'phaser';
import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';

/**
 * Movement type - what property the sine wave affects
 */
export type SineMovement =
  | 'horizontal'   // Oscillate X position
  | 'vertical'     // Oscillate Y position
  | 'angle'        // Oscillate rotation angle
  | 'scale'        // Oscillate scale (width & height)
  | 'scaleX'       // Oscillate X scale only
  | 'scaleY'       // Oscillate Y scale only
  | 'opacity';     // Oscillate alpha

/**
 * Wave type
 */
export type SineWave =
  | 'sine'         // Standard sine wave
  | 'triangle'     // Triangle wave
  | 'sawtooth'     // Sawtooth wave
  | 'square';      // Square wave (on/off)

export interface SineConfig extends BehaviorConfig {
  movement?: SineMovement;
  wave?: SineWave;
  period?: number;        // Time for one complete cycle (seconds)
  magnitude?: number;     // How far to move/scale/rotate
  periodRandom?: number;  // Random variation to period
  magnitudeRandom?: number; // Random variation to magnitude
  periodOffset?: number;  // Start offset in the cycle (0-1)
}

/**
 * SineBehavior - Oscillating movement behavior
 *
 * Equivalent to Construct 3's "Sine" behavior.
 * Makes an entity oscillate back and forth using sine wave motion.
 *
 * Example configs from C3 player:
 * - Sine (vertical): movement=vertical, period=1, magnitude=3
 * - Sine2 (horizontal): movement=horizontal, period=2, magnitude=-4
 */
export class SineBehavior extends Behavior {
  // Configuration
  public movement: SineMovement;
  public wave: SineWave;
  public period: number;
  public magnitude: number;

  // Internal state
  private time: number = 0;
  private prevValue: number = 0;
  private baseValue: number = 0;

  constructor(entity: Entity, config: SineConfig = {}) {
    super(entity, config);

    this.movement = config.movement ?? 'vertical';
    this.wave = config.wave ?? 'sine';
    this.period = config.period ?? 1;
    this.magnitude = config.magnitude ?? 10;

    // Apply random variations
    if (config.periodRandom) {
      this.period += (Math.random() - 0.5) * 2 * config.periodRandom;
    }
    if (config.magnitudeRandom) {
      this.magnitude += (Math.random() - 0.5) * 2 * config.magnitudeRandom;
    }

    // Apply period offset
    if (config.periodOffset) {
      this.time = config.periodOffset * this.period;
    }

    // Store base value
    this.baseValue = this.getCurrentValue();
  }

  /**
   * Get current value of the property we're animating
   */
  private getCurrentValue(): number {
    switch (this.movement) {
      case 'horizontal':
        return this.entity.x;
      case 'vertical':
        return this.entity.y;
      case 'angle':
        return this.entity.angle;
      case 'scale':
      case 'scaleX':
        return this.entity.scaleX;
      case 'scaleY':
        return this.entity.scaleY;
      case 'opacity':
        return this.entity.alpha;
      default:
        return 0;
    }
  }

  /**
   * Apply a value to the property we're animating
   */
  private applyDelta(delta: number): void {
    switch (this.movement) {
      case 'horizontal':
        this.entity.x += delta;
        break;
      case 'vertical':
        this.entity.y += delta;
        break;
      case 'angle':
        this.entity.angle += delta;
        break;
      case 'scale':
        this.entity.scaleX += delta;
        this.entity.scaleY += delta;
        break;
      case 'scaleX':
        this.entity.scaleX += delta;
        break;
      case 'scaleY':
        this.entity.scaleY += delta;
        break;
      case 'opacity':
        this.entity.alpha = Phaser.Math.Clamp(this.entity.alpha + delta, 0, 1);
        break;
    }
  }

  /**
   * Calculate wave value based on wave type
   */
  private calculateWaveValue(phase: number): number {
    // phase is 0-1 representing position in the cycle
    switch (this.wave) {
      case 'sine':
        return Math.sin(phase * Math.PI * 2);

      case 'triangle':
        // Triangle wave: goes 0 -> 1 -> 0 -> -1 -> 0
        const t = phase * 4;
        if (t < 1) return t;
        if (t < 3) return 2 - t;
        return t - 4;

      case 'sawtooth':
        // Sawtooth: goes 0 -> 1, then resets
        return phase * 2 - 1;

      case 'square':
        // Square wave: +1 for first half, -1 for second half
        return phase < 0.5 ? 1 : -1;

      default:
        return Math.sin(phase * Math.PI * 2);
    }
  }

  /**
   * Update the sine oscillation
   */
  update(time: number, delta: number): void {
    if (!this.enabled || this.period <= 0) return;

    const dt = delta / 1000;

    // Update time
    this.time += dt;

    // Calculate phase (0-1) in the current cycle
    const phase = (this.time % this.period) / this.period;

    // Calculate wave value (-1 to 1)
    const waveValue = this.calculateWaveValue(phase);

    // Calculate actual offset
    const currentValue = waveValue * this.magnitude;

    // Apply delta from previous value (avoids accumulation)
    const valueDelta = currentValue - this.prevValue;
    this.applyDelta(valueDelta);

    // Store for next frame
    this.prevValue = currentValue;
  }

  /**
   * Reset the sine wave to beginning
   */
  reset(): void {
    this.time = 0;
    this.prevValue = 0;
  }

  /**
   * Set new magnitude
   */
  setMagnitude(magnitude: number): void {
    this.magnitude = magnitude;
  }

  /**
   * Set new period
   */
  setPeriod(period: number): void {
    this.period = period;
  }

  /**
   * Get current phase (0-1)
   */
  getPhase(): number {
    if (this.period <= 0) return 0;
    return (this.time % this.period) / this.period;
  }
}
